// 🔒 LOCKED V1 — DO NOT MODIFY WITHOUT REVIEW
// Any new feature must be additive, not modifying existing structure

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useTranslation from '../../../hooks/useTranslation';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import AvenantModal from './AvenantModal';
import { formatCurrency as fmtCAD } from '../../../utils/formatCurrency';
import { statusVariant } from '../../../utils/statusVariant';
import Badge, { BADGE_VARIANTS } from '../../../components/ui/Badge';
import { extraDirectiveNumber } from '../../../utils/extraDirectiveNumber';

// 🔴 Les CLES de cet objet — draft, sent, signed, rejected — sont les valeurs
// stockees en base. Elles ne se traduisent ni ne se renomment JAMAIS. Le libelle
// affiche, lui, passe par statusLabel() juste en dessous.
const STATUS_CONFIG = {
  draft:    { bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400'  },
  sent:     { bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500'  },
  signed:   { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  rejected: { bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500'   },
};

/** Le libelle traduit d'un statut. La cle reste la valeur en base. */
const statusLabel = (t, status) => t(`changeOrders.status.${STATUS_CONFIG?.[status] ? status : 'draft'}`);

const STATUS_OPTIONS = ['draft', 'sent', 'signed', 'rejected'];

const formatCurrency = (value) => fmtCAD(value);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ─── StatusPill ────────────────────────────────────────────────────────────────
const StatusPill = ({ co, onStatusChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const ref = useRef(null);
  const currentStatus = co?.status || 'draft';
  const cfg = STATUS_CONFIG?.[currentStatus] || STATUS_CONFIG?.draft;

  const handleSelect = async (status) => {
    setOpen(false);
    if (status === currentStatus) return;
    setUpdating(true);
    try {
      await onStatusChange?.(co?.id, status);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="relative">
      <Badge
        as="button"
        ref={ref}
        variant={statusVariant('changeOrder', currentStatus)}
        onClick={(e) => { e?.stopPropagation(); !updating && setOpen((v) => !v); }}
        disabled={updating}
        className="cursor-pointer select-none transition-opacity hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed"
        title={t('changeOrders.actions.changeStatus')}
      >
        {statusLabel(t, currentStatus)}
        {updating
          ? <Icon name="Loader" size={10} className="animate-spin" />
          : <Icon name="ChevronDown" size={10} />
        }
      </Badge>
      <PortalDropdown
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={150}
        items={STATUS_OPTIONS?.map((status) => ({
          label: statusLabel(t, status),
          dot: BADGE_VARIANTS?.[statusVariant('changeOrder', status)]?.dot,
          active: currentStatus === status,
          onClick: () => handleSelect(status),
        }))}
      />
    </div>
  );
};

// ─── ActionsMenu (3-dot) — portal-based, never clips inside table overflow ─────
const ActionsMenu = ({ co, onEdit, onDelete, onViewDocuments, onViewDetail }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { t } = useTranslation();

  const items = [
    {
      // Aucun repli : t() rend la CLE BRUTE quand elle manque, jamais une valeur
      // falsy. Un « || 'View details' » ne pouvait donc jamais s'afficher.
      label: t('avenants.detail.view'),
      icon: 'Eye',
      onClick: () => onViewDetail?.(co),
    },
    {
      label: t('common.edit'),
      icon: 'Pencil',
      onClick: () => onEdit?.(co),
    },
    {
      label: t('common.delete'),
      icon: 'Trash2',
      onClick: () => onDelete?.(co?.id),
      variant: 'danger',
    },
  ];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title={t('changeOrders.actions.actions')}
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={180}
      />
    </>
  );
};

// ─── DocumentIconButton ────────────────────────────────────────────────────────
const DocumentIconButton = ({ co, projectId, onClick, refreshKey = 0 }) => {
  // `t` d'un parent n'est JAMAIS dans la portee d'un enfant : chaque composant
  // qui traduit doit appeler le hook lui-meme.
  const { t } = useTranslation();
  const [docCount, setDocCount] = useState(0);
  const [docNames, setDocNames] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);

  useEffect(() => {
    if (!co?.id || !projectId) return;
    const storagePath = `change-orders/${projectId}/${co?.id}`;
    supabase?.storage
      ?.from('contracts-documents')
      ?.list(storagePath, { limit: 20, sortBy: { column: 'name', order: 'asc' } })
      ?.then(({ data }) => {
        const filtered = (data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder');
        setDocCount(filtered?.length);
        setDocNames(filtered?.map(f => f?.name));
      })
      ?.catch(() => {});
  }, [co?.id, projectId, refreshKey]);

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => setShowTooltip(true), 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimeout?.current);
    setShowTooltip(false);
  };

  const hasDocuments = docCount > 0;

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={onClick}
        className="relative p-1 rounded transition-colors group"
        title={t('changeOrders.actions.documents')}
      >
        <Icon
          name="FileText"
          size={13}
          className={`transition-colors ${hasDocuments ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
        />
        {/* Badge */}
        <span
          className={`absolute -top-1 -right-1 text-[10px] rounded-full px-1 leading-tight font-medium ${
            hasDocuments ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-400'
          }`}
          style={{ minWidth: '14px', textAlign: 'center' }}
        >
          {docCount}
        </span>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white shadow-md rounded-md px-2 py-1.5 min-w-[120px] max-w-[180px] pointer-events-none">
          {hasDocuments ? (
            <div className="space-y-0.5">
              {docNames?.slice(0, 3)?.map((name, i) => (
                <p key={i} className="text-[10px] text-gray-600 truncate">{name}</p>
              ))}
              {docCount > 3 && (
                <p className="text-[10px] text-gray-400">+{docCount - 3} more</p>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-gray-500">{t('changeOrders.documents.none')}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DocumentsModal — same UI as Contracts, fetches by change_order_id ─────────
const DocumentsModal = ({ co, projectId, onClose, onDocumentsChanged }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [deleteConfirmFile, setDeleteConfirmFile] = useState(null);
  const fileInputRef = useRef(null);

  // Storage path uses change_order_id (same bucket as contracts)
  const storagePath = `change-orders/${projectId}/${co?.id}`;

  const getFileTypeIcon = (fileName) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf') return <Icon name="FileText" size={15} className="text-red-500 flex-shrink-0" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']?.includes(ext)) return <Icon name="Image" size={15} className="text-purple-500 flex-shrink-0" />;
    if (['xls', 'xlsx', 'csv']?.includes(ext)) return <Icon name="FileSpreadsheet" size={15} className="text-green-600 flex-shrink-0" />;
    if (['doc', 'docx']?.includes(ext)) return <Icon name="FileText" size={15} className="text-blue-500 flex-shrink-0" />;
    return <Icon name="File" size={15} className="text-gray-400 flex-shrink-0" />;
  };

  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const { data, error } = await supabase?.storage
        ?.from('contracts-documents')
        ?.list(storagePath, { limit: 100, sortBy: { column: 'name', order: 'asc' } });
      if (error) {
        console.error('Error listing files:', error);
        setFiles([]);
      } else {
        setFiles((data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder'));
      }
    } catch (err) {
      console.error('Unexpected error listing files:', err);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [storagePath]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const sanitizeFilename = (name) => {
    return name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.toLowerCase()?.replace(/\s+/g, '_')?.replace(/[^a-z0-9_.]/g, '')?.replace(/_+/g, '_');
  };

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploadError(null);
    setUploading(true);
    const MAX_SIZE = 20 * 1024 * 1024;
    const errors = [];
    for (const file of Array.from(fileList)) {
      if (file?.size > MAX_SIZE) { errors?.push(`${file?.name}: File too large (max 20MB)`); continue; }
      const sanitizedName = sanitizeFilename(file?.name);
      const filePath = `${storagePath}/${sanitizedName}`;
      try {
        const { error: uploadErr } = await supabase?.storage
          ?.from('contracts-documents')
          ?.upload(filePath, file, { upsert: true, cacheControl: '3600' });
        if (uploadErr) errors?.push(`${file?.name}: ${uploadErr?.message}`);
      } catch (err) {
        errors?.push(`${file?.name}: Unexpected error`);
      }
    }
    if (errors?.length) setUploadError(errors?.join('\n'));
    setUploading(false);
    await fetchFiles();
    onDocumentsChanged?.();
  };

  const handleFileInputChange = (e) => {
    uploadFiles(e?.target?.files);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => { e?.preventDefault(); setIsDragOver(false); uploadFiles(e?.dataTransfer?.files); };
  const handleDragOver = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragEnter = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const deleteFile = async (fileName) => {
    setDeletingFile(fileName);
    const filePath = `${storagePath}/${fileName}`;
    try {
      const { error } = await supabase?.storage?.from('contracts-documents')?.remove([filePath]);
      if (error) { alert('Could not delete file: ' + error?.message); }
      else {
        setFiles((prev) => prev?.filter((f) => f?.name !== fileName));
        onDocumentsChanged?.();
      }
    } catch (err) {
      alert('Unexpected error deleting file.');
    } finally {
      setDeletingFile(null);
    }
  };

  const openFile = async (fileName) => {
    const filePath = `${storagePath}/${fileName}`;
    try {
      const { data, error } = await supabase?.storage?.from('contracts-documents')?.createSignedUrl(filePath, 60 * 60);
      if (error) { alert('Could not open file: ' + error?.message); return; }
      if (data?.signedUrl) window.open(data?.signedUrl, '_blank');
    } catch (err) {
      alert('Unexpected error opening file.');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const subName = co?.subcontract?.subcontractor?.companyName || 'N/A';
  const coNum = co?.coNumber || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Icon name="FolderOpen" size={16} className="text-primary" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t('changeOrders.documents.panelTitle')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {subName}{coNum ? ` · ${coNum}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingFiles ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
            </div>
          ) : files?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="FileX" size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-xs text-gray-400">{t('changeOrders.documents.noneAttached')}</p>
            </div>
          ) : (
            <ul className="space-y-1">
              {files?.map((file) => (
                <li key={file?.name} className="group relative">
                  <button
                    onClick={() => openFile(file?.name)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    {getFileTypeIcon(file?.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate group-hover:text-primary transition-colors">
                        {file?.name}
                      </p>
                      {file?.metadata?.size && (
                        <p className="text-[10px] text-gray-400">{formatFileSize(file?.metadata?.size)}</p>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={(e) => { e?.stopPropagation(); setDeleteConfirmFile(file?.name); }}
                    disabled={deletingFile === file?.name}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('changeOrders.actions.deleteFile')}
                  >
                    {deletingFile === file?.name
                      ? <Icon name="Loader2" size={13} className="animate-spin text-red-400" />
                      : <Icon name="Trash2" size={13} />
                    }
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upload Zone */}
        <div className="px-5 pb-5 flex-shrink-0">
          {uploadError && (
            <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
              {uploadError}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileInputChange} />
          <div
            onClick={() => !uploading && fileInputRef?.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-all ${
              isDragOver ? 'border-primary bg-orange-50 scale-[1.01]' : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
            } ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                <p className="text-xs text-gray-500">{t('changeOrders.documents.uploading')}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Upload" size={20} className={isDragOver ? 'text-primary' : 'text-gray-400'} />
                <p className="text-xs font-medium text-gray-600">
                  Drop files here or <span className="text-primary">click to upload</span>
                </p>
                <p className="text-[10px] text-gray-400">PDF, Excel, Word supported · Max 20MB each</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirmFile}
        confirmVariant="danger"
        title={t('documents.delete.title')}
        message={deleteConfirmFile ? `${t('documents.delete.message')}` : t('documents.delete.message')}
        confirmLabel={t('documents.delete.confirm')}
        cancelLabel={t('documents.delete.cancel')}
        onConfirm={async () => { const name = deleteConfirmFile; setDeleteConfirmFile(null); await deleteFile(name); }}
        onCancel={() => setDeleteConfirmFile(null)}
      />
    </div>
  );
};

// ─── ChangeOrdersTable ─────────────────────────────────────────────────────────
const ChangeOrdersTable = ({ changeOrders, onEdit, onDelete, onSend, onMarkAsSigned, onStatusChange, sortConfig, onSort, onNavigateToExtra, projectId, onNoteUpdate, subcontracts = [], projectExtras = [], onSaveAvenant }) => {
  const { t } = useTranslation();
  const [documentsco, setDocumentsCo] = useState(null);
  const [docRefreshKeys, setDocRefreshKeys] = useState({});
  const [detailAvenant, setDetailAvenant] = useState(null);
  // Fiche d'avenant : consultation (ligne, « Voir ») ou modification (« Modifier »).
  const [detailMode, setDetailMode] = useState('view');

  // Note modal state
  const [noteModalCo, setNoteModalCo] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const openNoteModal = (co) => {
    setNoteModalCo(co);
    setNoteText(co?.notes || '');
  };

  const closeNoteModal = () => {
    setNoteModalCo(null);
    setNoteText('');
    setNoteSaving(false);
  };

  const handleNoteSave = async () => {
    if (!noteModalCo || !onNoteUpdate) return;
    setNoteSaving(true);
    const success = await onNoteUpdate(noteModalCo?.id, noteText);
    if (success) {
      closeNoteModal();
    } else {
      setNoteSaving(false);
    }
  };

  // La colonne n_co testait `language === 'FR'` en MAJUSCULES, alors que
  // LanguageContext stocke 'fr' : la branche francaise n'etait jamais atteinte et
  // l'en-tete affichait « C.O. » meme en francais. La comparaison disparait — la
  // cle i18n porte « O.C. » en fr et « C.O. » en en, comme les sept autres.
  const columns = [
    { key: 'co_number',     label: t('changeOrders.table.coNumber') },
    { key: 'subcontractor', label: t('changeOrders.table.subcontractor') },
    { key: 'n_co',          label: t('changeOrders.table.nCo') },
    { key: 'avenant_title', label: t('changeOrders.table.avenantTitle') },
    { key: 'amount',        label: t('changeOrders.table.amount'), alignRight: true },
    { key: 'status',        label: t('changeOrders.table.status') },
    { key: 'notes',         label: t('changeOrders.table.notes'), alignCenter: true },
    { key: 'actions',       label: t('changeOrders.table.actions') },
  ];

  const SortIcon = ({ colKey }) => {
    if (sortConfig?.key !== colKey) return <Icon name="ChevronsUpDown" size={12} className="text-gray-400 ml-1 inline" />;
    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={12} className="text-primary ml-1 inline" />
      : <Icon name="ChevronDown" size={12} className="text-primary ml-1 inline" />;
  };

  const sortableKeys = ['co_number', 'subcontractor', 'amount', 'status'];

  return (
    <>
      <div>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              {columns?.map(col => (
                <th
                  key={col?.key}
                  className={`px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap ${col?.alignRight ? 'text-right' : col?.alignCenter ? 'text-center' : 'text-left'} ${sortableKeys?.includes(col?.key) ? 'cursor-pointer select-none hover:bg-gray-100 transition-colors' : ''}`}
                  onClick={() => sortableKeys?.includes(col?.key) && onSort?.(col?.key)}
                >
                  {sortableKeys?.includes(col?.key) ? (
                    <div className={`flex items-center gap-1 ${col?.alignRight ? 'justify-end' : col?.alignCenter ? 'justify-center' : ''}`}>
                      <span>{col?.label}</span>
                      <SortIcon colKey={col?.key} />
                    </div>
                  ) : (
                    col?.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {changeOrders?.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                  <Icon name="FileText" size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">{t('changeOrders.empty.title')}</p>
                  <p className="text-xs mt-1">{t('changeOrders.empty.hint')}</p>
                </td>
              </tr>
            ) : (
              changeOrders?.map(co => {
                const subName = co?.subcontract?.subcontractor?.companyName || '—';
                const contactName = co?.subcontract?.subcontractor?.contactPerson || '';
                console.log('[ChangeOrders] subcontractor data:', {
                  id: co?.id,
                  coNumber: co?.coNumber,
                  companyName: subName,
                  contactPerson: co?.subcontract?.subcontractor?.contactPerson,
                  contactName,
                });
                // Titre de l'avenant (colonne description), sinon le titre de l'extra source.
                const avenantTitle = co?.description?.trim() || co?.sourceExtra?.title || '—';
                const avatarInitials = subName !== '—' ? subName?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)?.toUpperCase()
                  : 'SC';
                const hasNote = !!(co?.notes?.trim());

                return (
                  <tr
                    key={co?.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => { setDetailMode('view'); setDetailAvenant(co); }}
                  >
                    {/* CO # — mauve number + cost code title below */}
                    <td className="px-4 py-3">
                      {co?.coNumber ? (
                        <div>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#e8e0f0] text-[#7c5fa6] text-[10px] font-medium leading-tight">
                            {co?.coNumber}
                          </span>
                          {co?.subcontract?.projectCost?.description && (
                            <p className="text-gray-500 leading-tight mt-0.5" style={{ fontSize: '10.2px' }} title={co?.subcontract?.projectCost?.description}>
                              {co?.subcontract?.projectCost?.description}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {/* Subcontractor — avatar + company + contact */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold text-[10px] flex-shrink-0">
                          {avatarInitials}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900 leading-tight whitespace-nowrap">
                            {subName}
                          </p>
                          {contactName && (
                            <p className="text-xs text-gray-500 leading-tight mt-0.5">
                              {contactName}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* N° CO — clickable change order number */}
                    <td className="px-4 py-3">
                      {/* N° de directive de l'extra source : directive liee, sinon co_number */}
                      {extraDirectiveNumber(co?.sourceExtra) ? (
                        <span
                          onClick={(e) => { e?.stopPropagation(); onNavigateToExtra?.(co?.sourceExtra?.id); }}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 text-orange-600 cursor-pointer hover:bg-orange-200 transition-colors"
                          title={`Open: ${extraDirectiveNumber(co?.sourceExtra)}`}
                        >
                          {extraDirectiveNumber(co?.sourceExtra)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {/* Titre de l'avenant + sous-ligne description (meme typo que
                        les directives et les extras) — texte seul, sans interaction */}
                    <td className="px-4 py-3 min-w-0" style={{ minWidth: '280px', maxWidth: '380px' }}>
                      <p className="text-xs text-gray-700 leading-tight truncate" title={avenantTitle}>{avenantTitle}</p>
                      {co?.notes?.trim() && (
                        <p className="text-[10px] text-gray-400 leading-tight truncate mt-0.5" title={co?.notes}>{co?.notes}</p>
                      )}
                    </td>
                    {/* Amount — right aligned */}
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${(parseFloat(co?.amount) || 0) < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                      {formatCurrency(co?.amount)}
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusPill co={co} onStatusChange={onStatusChange} />
                    </td>
                    {/* Notes — note icon, orange if note exists */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e?.stopPropagation(); openNoteModal(co); }}
                        title={hasNote ? t('changeOrders.actions.editNote') : t('changeOrders.actions.addNote')}
                        className="p-1 rounded hover:bg-gray-100 transition-colors"
                      >
                        <Icon
                          name="FileText"
                          size={15}
                          className={hasNote ? 'text-primary' : 'text-gray-300'}
                        />
                      </button>
                    </td>
                    {/* Actions — document icon + 3-dot menu */}
                    <td className="px-4 py-3 text-left" onClick={(e) => e?.stopPropagation()}>
                      <div className="flex items-end justify-end gap-2 h-full">
                        <div className="relative flex items-end">
                          <DocumentIconButton
                            co={co}
                            projectId={projectId}
                            onClick={() => setDocumentsCo(co)}
                            refreshKey={docRefreshKeys?.[co?.id] || 0}
                          />
                        </div>
                        <div className="flex items-end">
                          <ActionsMenu
                            co={co}
                            onEdit={onSaveAvenant ? (c) => { setDetailMode('edit'); setDetailAvenant(c); } : onEdit}
                            onDelete={onDelete}
                            onViewDocuments={(co) => setDocumentsCo(co)}
                            onViewDetail={(co) => { setDetailMode('view'); setDetailAvenant(co); }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Note Modal — same design as Contract Note */}
      {noteModalCo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeNoteModal} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-gray-900">{t('changeOrder.note.title')}</h3>
              </div>
              <button
                onClick={closeNoteModal}
                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={15} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {noteModalCo?.subcontract?.subcontractor?.companyName || 'N/A'}
              {noteModalCo?.coNumber ? ` · ${noteModalCo?.coNumber}` : ''}
            </p>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e?.target?.value)}
              placeholder={t('changeOrder.note.placeholder')}
              rows={4}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none transition-colors"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={closeNoteModal}
                disabled={noteSaving}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleNoteSave}
                disabled={noteSaving}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {noteSaving ? (
                  <>
                    <Icon name="Loader2" size={12} className="animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Documents Modal */}
      {documentsco && (
        <DocumentsModal
          co={documentsco}
          projectId={projectId}
          onClose={() => setDocumentsCo(null)}
          onDocumentsChanged={() => setDocRefreshKeys(prev => ({ ...prev, [documentsco?.id]: (prev?.[documentsco?.id] || 0) + 1 }))}
        />
      )}
      {/* Avenant Detail Modal */}
      {detailAvenant && (
        <AvenantModal
          avenant={detailAvenant}
          mode={detailMode}
          subcontracts={subcontracts}
          projectExtras={projectExtras}
          projectId={projectId}
          onSave={onSaveAvenant}
          onClose={() => setDetailAvenant(null)}
        />
      )}
    </>
  );
};

export default ChangeOrdersTable;
